import React, { Fragment, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { WizardProvider, useWizard } from '../context/WizardContext';
import { useLanguage } from '../context/LanguageContext';
import WizardStepRouter from '../components/wizard/WizardStepRouter';
import { useWizardPersistence } from '../hooks/useWizardPersistence';
import { useWizardStepAnalytics } from '../hooks/useWizardStepAnalytics';
import {
  TOTAL_WIZARD_STEPS,
  WIZARD_STEP_KEYS,
  WIZARD_STEP_FALLBACK_LABELS
} from '../constants/wizardSteps';

/**
 * Inner Wizard Page component that uses the wizard context
 */
const WizardPageContent = () => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();
  const wizardRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const hasCheckedProgress = useRef(false);

  // Initialize persistence hook
  const {
    hasStoredProgress,
    saveProgress,
    clearProgress,
    restoreProgress,
    dismissProgress,
    getStoredStep,
    getTimeSinceLastSave
  } = useWizardPersistence();

  const stepLabels = useMemo(
    () =>
      WIZARD_STEP_KEYS.map((key, index) =>
        t(key, WIZARD_STEP_FALLBACK_LABELS[index] || key)
      ),
    [t]
  );

  // Step analytics tracking
  const { markComplete: markAnalyticsComplete } = useWizardStepAnalytics(
    state.currentStep,
    stepLabels[state.currentStep - 1] || ''
  );

  // Check for saved progress on mount (only once)
  useEffect(() => {
    if (!hasCheckedProgress.current && hasStoredProgress && getStoredStep() > 1) {
      setShowResumeModal(true);
      hasCheckedProgress.current = true;
    }
  }, [hasStoredProgress, getStoredStep]);

  // Auto-save progress on state changes (debounced by the hook)
  useEffect(() => {
    // Only save if user has progressed past step 1
    if (state.currentStep > 1 && !state.isCompleted) {
      saveProgress(state);
    }
  }, [state, saveProgress]);

  // Handle resume progress
  const handleResumeProgress = useCallback(() => {
    const savedState = restoreProgress();
    if (savedState) {
      actions.hydrateState(savedState);
    }
    setShowResumeModal(false);
  }, [restoreProgress, actions]);

  // Handle dismiss progress (start fresh)
  const handleDismissProgress = useCallback(() => {
    dismissProgress();
    setShowResumeModal(false);
  }, [dismissProgress]);

  // Handle wizard completion - clear saved progress
  const handleWizardComplete = useCallback(() => {
    clearProgress();
    actions.completeWizard();
  }, [clearProgress, actions]);

  const handleNext = (data) => {
    console.log('Wizard step completed:', data);
    if (state.currentStep < TOTAL_WIZARD_STEPS) {
      actions.setCurrentStep(state.currentStep + 1);
    }
  };

  const handlePrevious = () => {
    console.log('Navigate to previous step');
    // Move to the previous step
    if (state.currentStep > 1) {
      actions.setCurrentStep(state.currentStep - 1);
    }
  };

  // Enhanced scroll behavior with smooth animation and progress tracking
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Immediate scroll to top on step change
      window.scrollTo(0, 0);

      // Backup smooth scroll just in case
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: 'auto' // 'auto' is instant, 'smooth' can be interrupted better to be instant for step changes
        });
      });

      // Track scroll progress for visual feedback
      const trackScrollProgress = () => {
        const scrollTop = window.pageYOffset;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const progress = (scrollTop / docHeight) * 100;
        setScrollProgress(Math.min(progress, 100));
      };

      window.addEventListener('scroll', trackScrollProgress);
      return () => window.removeEventListener('scroll', trackScrollProgress);
    }
  }, [state.currentStep]);

  // Scroll to top function
  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div ref={wizardRef} className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-6 sm:py-8">
      {/* Resume Progress Modal */}
      {showResumeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8 animate-in fade-in zoom-in duration-300">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {t('wizard.resume.title', 'Resume Your Progress?')}
              </h2>
              <p className="text-gray-600">
                {t('wizard.resume.description', 'We found your saved progress from {{time}}. You were on step {{step}} of {{total}}.', {
                  time: getTimeSinceLastSave() || 'earlier',
                  step: getStoredStep(),
                  total: TOTAL_WIZARD_STEPS
                })}
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleResumeProgress}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('wizard.resume.continue', 'Continue Where I Left Off')}
              </button>

              <button
                onClick={handleDismissProgress}
                className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
              >
                {t('wizard.resume.startFresh', 'Start Fresh')}
              </button>
            </div>

            <p className="mt-4 text-xs text-gray-500 text-center">
              {t('wizard.resume.note', 'Your saved progress will expire after 24 hours')}
            </p>
          </div>
        </div>
      )}

      {/* Scroll Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50" role="progressbar" aria-label="Wizard scroll progress">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300 ease-out"
          style={{ width: `${scrollProgress}%` }}
          aria-valuenow={scrollProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-10">
        {/* Progress Indicator */}
        <div className="mb-6 sm:mb-8">
          {/* Mobile: Compact Progress Bar */}
          <div className="sm:hidden sticky top-0 z-30 -mx-4 px-4 pt-4 pb-3 bg-white/90 backdrop-blur border-b border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
                  current: state.currentStep,
                  total: TOTAL_WIZARD_STEPS
                })}
              </span>
              <span className="text-xs text-gray-500">
                {stepLabels[state.currentStep - 1]}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(state.currentStep / TOTAL_WIZARD_STEPS) * 100}%` }}
              />
            </div>
            <div className="flex justify-center gap-1.5 mt-2">
              {Array.from({ length: TOTAL_WIZARD_STEPS }, (_, idx) => idx + 1).map((step) => (
                <div
                  key={step}
                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${step === state.currentStep
                    ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                    : step < state.currentStep
                      ? 'bg-blue-400 text-white'
                      : 'bg-gray-200 text-gray-400'
                    }`}
                >
                  {step}
                </div>
              ))}
            </div>
          </div>

          {/* Desktop: Full Progress Indicator */}
          <div className="hidden sm:block">
            <div className="flex items-center justify-between w-full">
              {Array.from({ length: TOTAL_WIZARD_STEPS }, (_, idx) => idx + 1).map((step) => (
                <Fragment key={step}>
                  <div className="flex items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${step <= state.currentStep
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                        }`}
                    >
                      {step}
                    </div>
                    {step < TOTAL_WIZARD_STEPS && (
                      <div
                        className={`w-8 h-1 mx-1 transition-all duration-200 ${step < state.currentStep
                          ? 'bg-blue-600'
                          : 'bg-gray-200'
                          }`}
                      />
                    )}
                  </div>
                </Fragment>
              ))}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-600 w-full px-2">
              {stepLabels.map((label, idx) => (
                <span key={idx}>{label}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Page Header */}
        <div className="text-center mt-6 sm:mt-0 mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {t('wizard.header.title', 'Lead Qualification Wizard')}
          </h1>
          <p className="text-base sm:text-xl text-gray-600 w-full">
            {t('wizard.header.subtitle', 'Find your best immigration pathway with our AI-powered qualification wizard. Answer a few questions about your background and preferences to get personalized recommendations.')}
          </p>
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <div className="inline-flex items-center rounded-full bg-blue-100 px-4 py-2 text-sm font-medium text-blue-800">
              <span className="mr-2">⏱️</span> {t('wizard.header.badgeTime', 'Complete in about 6 minutes')}
            </div>
            <div className="inline-flex items-center rounded-full bg-green-100 px-4 py-2 text-sm font-medium text-green-800">
              <span className="mr-2">✅</span> {t('wizard.header.badgeReview', 'Advisors review your answers before the call')}
            </div>
          </div>
        </div>

        {/* Wizard Content */}
        <div className="border border-gray-200 rounded-2xl bg-white shadow-sm overflow-hidden -mx-2 sm:mx-0">
          <div className="p-4 sm:p-8 lg:p-10 border-b border-gray-100">
            <WizardStepRouter
              currentStep={state.currentStep}
              onNext={handleNext}
              onPrevious={handlePrevious}
            />
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 sm:mt-8 text-center">
          <div className="inline-flex items-center justify-center px-4 py-3 sm:px-6 sm:py-4 glass rounded-lg">
            <svg className="w-5 h-5 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-blue-800 text-sm">
              {t('wizard.help.text', 'Need help? Contact our immigration specialists at')} {' '}
              <a href="mailto:info@ravanaindustries.com" className="text-blue-600 hover:text-blue-800 underline">
                {t('wizard.help.email', 'info@ravanaindustries.com')}
              </a>
            </span>
          </div>
        </div>
      </div>

      {/* Floating Scroll-to-Top Button */}
      {scrollProgress > 20 && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-20 right-4 z-40 w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300"
          aria-label="Scroll to top of wizard"
          title="Scroll to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

/**
 * Wizard Page - Main entry point for the Lead Qualification Wizard
 * This page wraps the wizard components with the necessary context provider
 */
const WizardPage = () => {
  return (
    <WizardProvider>
      <WizardPageContent />
    </WizardProvider>
  );
};

export default WizardPage;
